import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth-context";
import { api, fmtCurrency } from "../../lib/api";
import Logo from "../../components/Logo";
import NotificationBell from "../../components/NotificationBell";
import {
    LineChart, Line, ResponsiveContainer, AreaChart, Area, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Plus, ChevronDown, ChevronRight, FileText, Package, ShoppingCart, Users as UsersIcon, MoreHorizontal, FilePlus2, Receipt, Menu } from "lucide-react";

const RECOMMENDED = [
    { name: "BizCredit", title: "BizCredit™", subtitle: "Get working capital for your business", limit: "Up to ₦5,000,000", color: "#0066FF", bg: "bg-blue-50", btn: "bg-[#0066FF] hover:bg-[#0052cc]", action: "Apply Now" },
    { name: "TradeCredit", title: "TradeCredit™", subtitle: "Finance your invoices & grow faster", limit: "Up to ₦10,000,000", color: "#06B6D4", bg: "bg-cyan-50", btn: "bg-[#06B6D4] hover:bg-[#0891B2]", action: "Apply Now" },
    { name: "StockFinance", title: "StockFinance™", subtitle: "Unlock value from your inventory", limit: "Up to ₦7,500,000", color: "#FF6B00", bg: "bg-orange-50", btn: "bg-[#FF6B00] hover:bg-[#e55f00]", action: "Apply Now" },
];

export default function BusinessHome() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);

    useEffect(() => {
        api.get("/business/overview").then((r) => setOverview(r.data)).catch(() => {});
    }, []);

    const cashflowData = (overview?.cashflow || []).map((c) => ({ month: c.month, value: c.sales - c.expenses }));

    return (
        <div data-testid="business-home">
            <div className="gradient-business gradient-noise px-5 pt-10 pb-8 text-white">
                <div className="flex items-center justify-between">
                    <button data-testid="business-menu" className="size-10 rounded-full bg-white/15 flex items-center justify-center">
                        <Menu size={20} />
                    </button>
                    <Logo size={32} withText={false} />
                    <NotificationBell variant="dark" testid="business-bell" />
                </div>
                <div className="mt-5">
                    <div className="text-sm text-white/80">Welcome, {user?.full_name?.split(" ")[0]}</div>
                    <div className="font-display text-2xl font-semibold flex items-center gap-2 mt-0.5" data-testid="business-name">
                        {user?.business_name || overview?.business?.name || "Your Business"}
                        <ChevronDown size={20} />
                    </div>
                </div>
            </div>

            <div className="px-5 -mt-4 space-y-4">
                {/* Business Overview */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="business-overview-card">
                    <div className="flex justify-between items-center">
                        <div className="font-display font-semibold text-[#0A1930]">Business Overview</div>
                        <div className="text-xs font-semibold text-[#0066FF] flex items-center gap-1">This Month <ChevronDown size={12} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <Stat label="Total Sales" value={fmtCurrency(overview?.total_sales || 0, "NGN", { compact: true })} delta="+18.0%" />
                        <Stat label="Total Expenses" value={fmtCurrency(overview?.total_expenses || 0, "NGN", { compact: true })} delta="-8.2%" deltaPositive />
                        <Stat label="Net Cashflow" value={fmtCurrency(overview?.net_cashflow || 0, "NGN", { compact: true })} delta="+22.4%" />
                        <Stat label="Profit" value={fmtCurrency(overview?.profit || 0, "NGN", { compact: true })} delta="+14.1%" />
                    </div>
                </div>

                {/* Cashflow trend */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="cashflow-chart">
                    <div className="flex justify-between items-center mb-2">
                        <div className="font-display font-semibold text-[#0A1930]">Cashflow Trend</div>
                        <div className="text-xs font-semibold text-[#0066FF] flex items-center gap-1">Last 6 Months <ChevronDown size={12} /></div>
                    </div>
                    <ResponsiveContainer width="100%" height={140}>
                        <AreaChart data={cashflowData}>
                            <defs>
                                <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#00D084" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#00D084" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} fontSize={11} />
                            <YAxis hide />
                            <Tooltip formatter={(v) => fmtCurrency(v, "NGN", { compact: true })} />
                            <Area type="monotone" dataKey="value" stroke="#00D084" strokeWidth={2.5} fill="url(#cf)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Recommended */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-semibold text-[#0A1930]">Recommended for You</h2>
                        <Link to="/business/loans" className="text-xs font-semibold text-[#0066FF]" data-testid="see-all-business-loans">View All</Link>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {RECOMMENDED.map((r) => (
                            <Link to={`/business/loans/${r.name}`} key={r.name} data-testid={`biz-product-${r.name}`} className={`block ${r.bg} rounded-2xl p-3 border border-slate-200/60 tap-feedback`}>
                                <div
                                    className="size-9 rounded-lg flex items-center justify-center font-display font-bold text-white"
                                    style={{ background: r.color }}
                                >
                                    {r.title.slice(0, 1)}
                                </div>
                                <div className="font-display font-semibold text-xs mt-2" style={{ color: r.color }}>{r.title}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{r.subtitle}</div>
                                <div className="text-[10px] font-bold mt-2" style={{ color: r.color }}>{r.limit}</div>
                                <div className={`mt-2 text-center text-[11px] font-bold text-white rounded-md py-1 ${r.btn}`}>{r.action}</div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Quick Actions */}
                <div>
                    <h2 className="font-display font-semibold text-[#0A1930] mb-3">Quick Actions</h2>
                    <div className="grid grid-cols-4 gap-2">
                        <QA icon={Plus} label="Request Loan" to="/business/loans" color="#00D084" />
                        <QA icon={FilePlus2} label="Create Invoice" to="/business/more/invoices" color="#0066FF" />
                        <QA icon={ShoppingCart} label="Record Sale" to="/business/more/merchantos" color="#FF6B00" />
                        <QA icon={MoreHorizontal} label="More" to="/business/more" color="#7000FF" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, delta, deltaPositive }) {
    const positive = deltaPositive ?? (delta || "").startsWith("+");
    return (
        <div className="bg-slate-50 rounded-xl p-3" data-testid={`stat-${label.replace(/\s/g, "")}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
            <div className="font-display text-base font-bold text-[#0A1930] mt-1">{value}</div>
            <div className={`text-[10px] font-bold mt-0.5 ${positive ? "text-emerald-600" : "text-rose-600"}`}>{delta}</div>
        </div>
    );
}

function QA({ icon: Icon, label, to, color }) {
    return (
        <Link to={to} data-testid={`qa-${label.replace(/\s/g, "")}`} className="bg-white rounded-2xl border border-slate-200 p-3 text-center tap-feedback">
            <div className="size-9 rounded-lg flex items-center justify-center mx-auto" style={{ background: `${color}15`, color }}>
                <Icon size={18} />
            </div>
            <div className="text-[10px] font-bold mt-1.5 leading-tight">{label}</div>
        </Link>
    );
}
